import { useModal } from '@ebay/nice-modal-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { MessageModal, ToastMessage } from '../../components/common'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import { queryKeys } from '../queries/keys'

const useClaimTreasureBonus = () => {
  const { t } = useTranslation('common')
  const messageModal = useModal(MessageModal)
  const queryClient = useQueryClient()
  const toastMessage = useModal(ToastMessage)
  const username = useBoundStore(state => state.user.username)
  const usernameRef = useRef(username)
  // Synchronous in-flight guard. The claim button's `disabled` attribute is
  // derived from `isLoading`, which only reaches the DOM after a re-render, so
  // a rapid double-click (reported ~33ms apart) fires two POSTs before the
  // button visibly disables. This ref flips synchronously on the first `claim`
  // and isn't released until `onSettled` (after the success refetch), so a
  // second click in the same render window is dropped.
  const isClaimingRef = useRef(false)

  useEffect(() => {
    usernameRef.current = username
  }, [username])

  const mutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post<ApiResponse<string>>('/ajax_data.php', {
        type: 'claim_treasure_bonus',
        id,
      })
      if (res.data.status !== 1) {
        throw new Error(res.data.info)
      }
      return res.data.info
    },
    onSuccess: async info => {
      toastMessage.show({ text: info, isValid: true })
      // Await the refetch so the mutation stays `isLoading` until the bonus
      // list is fresh. Otherwise the claim button re-enables the instant the
      // POST resolves — while the just-claimed id is still on screen — letting
      // a rapid second click reclaim the same bonus during the refetch gap.
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.available_bonuses, usernameRef.current],
      })
    },
    onError: err => {
      messageModal.show({
        title: '',
        message:
          (err instanceof Error && err.message) ||
          t('something-went-wrong'),
      })
    },
    onSettled: () => {
      isClaimingRef.current = false
    },
  })

  const claim = (id: number) => {
    if (isClaimingRef.current) return
    isClaimingRef.current = true
    mutation.mutate(id)
  }

  return { ...mutation, claim }
}

export default useClaimTreasureBonus
